// Continuous color pulse for search-result highlights in the context/location modal only.

const STYLE_PALETTE = {
  entry: { stroke: '#059669', passiveFill: 0.05, passiveStroke: 0.66, activeFillMin: 0.25, activeFillMax: 0.25 },
  related: { stroke: '#2563eb', passiveFill: 0.12, passiveStroke: 0.66, activeFillMin: 0.18, activeFillMax: 0.27 },
  subentry: { stroke: '#f59e0b', passiveFill: 0.05, passiveStroke: 0.66, activeFillMin: 0.16, activeFillMax: 0.26 }
};

const CATEGORY_RGB_MAP = {
  entry: '5, 150, 105',
  related: '37, 99, 235',
  subentry: '245, 158, 11'
};

const IDLE_FOCUSED_GLOW_BLUR = { entry: 5, related: 4, subentry: 4 };
const SHADOW_BLUR_BASE = { entry: 15, related: 14, subentry: 14 };
const SHADOW_BLUR_SPAN = { entry: 21, related: 20, subentry: 20 };

export const pulseColorIntensityAtElapsed = (elapsedMs) => {
  const wave = (Math.sin(elapsedMs * 0.005) + 1) / 2;

  return {
    fillIntensity: 0.15 + (wave * 0.85),
    glowIntensity: 0.2 + (wave * 0.8)
  };
};

export class SearchResultHighlightPulseLoop {
  constructor({ onTick }) {
    this.onTick = onTick;
    this.frameId = null;
    this.startTime = null;
  }

  start() {
    if (this.frameId) {
      return;
    }

    this.startTime = performance.now();

    const tick = (now) => {
      const { fillIntensity, glowIntensity } = pulseColorIntensityAtElapsed(now - this.startTime);
      this.onTick(fillIntensity, glowIntensity);
      this.frameId = requestAnimationFrame(tick);
    };

    this.frameId = requestAnimationFrame(tick);
  }

  stop() {
    if (!this.frameId) {
      return;
    }

    cancelAnimationFrame(this.frameId);
    this.frameId = null;
    this.startTime = null;
  }
}

export const buildPulsingSelectedHighlightStyle = ({
  categoryStyleConfig,
  resolvedCategoryKey,
  categoryRgb,
  fillIntensity,
  glowIntensity,
  isHovered = false
}) => {
  const fillRange = Math.max(categoryStyleConfig.activeFillMax - categoryStyleConfig.activeFillMin, 0.32);
  const fillAlpha = categoryStyleConfig.activeFillMin + (fillRange * fillIntensity);
  const selectedBorderWidth = resolvedCategoryKey === 'entry' ? 2 : (isHovered ? 2.25 : 2);
  const blurKey = STYLE_PALETTE[resolvedCategoryKey] ? resolvedCategoryKey : 'related';
  const idleGlow = IDLE_FOCUSED_GLOW_BLUR[blurKey] || 4;
  const maxGlow = SHADOW_BLUR_BASE[blurKey] || 14;
  const shadowBlur = isHovered
    ? (SHADOW_BLUR_BASE[blurKey] + (glowIntensity * SHADOW_BLUR_SPAN[blurKey]))
    : (idleGlow + ((maxGlow + 6 - idleGlow) * glowIntensity));

  return {
    fill: `rgba(${categoryRgb}, ${fillAlpha.toFixed(3)})`,
    stroke: categoryStyleConfig.stroke,
    lineWidth: selectedBorderWidth,
    shadowBlur,
    shadowRgbCsv: categoryRgb,
    glowAlpha: 0.12 + (glowIntensity * 0.28)
  };
};

export const resolvePulseCategoryStyle = ({
  highlightCategory,
  dictionaryCategoryColor,
  parseColorToRgb
}) => {
  const parsedDictionaryColor = parseColorToRgb(dictionaryCategoryColor);
  const usesCustomDictionaryColor = highlightCategory === 'custom' && parsedDictionaryColor;
  const resolvedCategoryKey = STYLE_PALETTE[highlightCategory] ? highlightCategory : 'related';
  const categoryStyleConfig = usesCustomDictionaryColor
    ? { stroke: parsedDictionaryColor.stroke, passiveFill: 0.2, passiveStroke: 0.64, activeFillMin: 0.26, activeFillMax: 0.38 }
    : STYLE_PALETTE[resolvedCategoryKey];
  const categoryRgb = usesCustomDictionaryColor
    ? `${parsedDictionaryColor.r}, ${parsedDictionaryColor.g}, ${parsedDictionaryColor.b}`
    : CATEGORY_RGB_MAP[resolvedCategoryKey];

  return { categoryStyleConfig, resolvedCategoryKey, categoryRgb };
};

export class SearchResultHighlightPulseManager {
  constructor({ isEnabled, redrawTarget }) {
    this.isEnabled = isEnabled;
    this.redrawTarget = redrawTarget;
    this.targets = [];
    this.loop = null;
    this.fillIntensity = null;
    this.glowIntensity = null;
  }

  stop() {
    if (this.loop) {
      this.loop.stop();
      this.loop = null;
    }

    this.targets = [];
    this.fillIntensity = null;
    this.glowIntensity = null;
  }

  registerTarget(target) {
    if (!this.isEnabled()) {
      return;
    }

    this.targets.push(target);
  }

  startIfNeeded() {
    if (!this.isEnabled()) {
      return;
    }

    const hasSearchResult = this.targets.some(
      target => target.wordsToHighlight.some(word => word.is_search_result)
    );

    if (!hasSearchResult || this.loop) {
      return;
    }

    this.loop = new SearchResultHighlightPulseLoop({
      onTick: (fillIntensity, glowIntensity) => this.tick(fillIntensity, glowIntensity)
    });
    this.loop.start();
  }

  isActive() {
    return this.isEnabled() && this.loop !== null && this.fillIntensity !== null;
  }

  getAnimationState(isSelectedWord) {
    if (!isSelectedWord || !this.isActive()) {
      return null;
    }

    return {
      fillIntensity: this.fillIntensity,
      glowIntensity: this.glowIntensity
    };
  }

  tick(fillIntensity, glowIntensity) {
    if (!this.isEnabled()) {
      this.stop();
      return;
    }

    this.fillIntensity = fillIntensity;
    this.glowIntensity = glowIntensity;

    this.targets.forEach((target) => {
      this.redrawTarget(target);
    });
  }
}
